import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { Router } from '@angular/router';
import { ConfiguratorTransactionalEmailService, DatastoreDto, Pageable, Partner, TransactionalEmailConfDTO } from '@tj/tj-dmp-lib-api';
import { ConfirmationService, LazyLoadEvent, MenuItem, MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

@Component({
    templateUrl: './transactional-email.component.html',
    styleUrls: ['./transactional-email.component.scss'],
    standalone: false
})
export class TransactionalEmailComponent implements OnInit {
    breadcrumb = [
        { label: this.translate.instant('menu.marketing.label') },
        { label: this.translate.instant('menu.workflow.marketing-emails') }
    ];
    description = this.translate.instant('transactionalEmail.list.description');

    datastore: DatastoreDto;
    partner: Partner;

    emails: TransactionalEmailConfDTO[] = [];
    totalRecords: number;

    actualEvent: LazyLoadEvent = {
        first: 0,
        rows: 25
    };

    state: { message: string };

    selectedEmails: TransactionalEmailConfDTO[] = [];
    uploadingFile = false;
    exportMenuItems: MenuItem[] = [];

    @ViewChild('emailFileInput') emailFileInput: ElementRef;

    constructor(private emailService: ConfiguratorTransactionalEmailService, private confirmationService: ConfirmationService, private messageService: MessageService, private router: Router, private readonly translate: TranslateService) {
        const navigation = this.router.getCurrentNavigation();
        const state = navigation?.extras?.state as { message: string } | undefined;
        this.state = state;
        this.translate.onLangChange.subscribe(() => this.rebuildTranslatedHeader());
    }

    private rebuildTranslatedHeader(): void {
        this.breadcrumb = [
            { label: this.translate.instant('menu.marketing.label') },
            { label: this.translate.instant('menu.workflow.marketing-emails') }
        ];
        this.description = this.translate.instant('transactionalEmail.list.description');
    }

    ngOnInit(): void {
        if (this.state?.message === 'success_created') {
            setTimeout(() => this.messageService.add({ severity: 'success', summary: this.translate.instant('datastore.successSummary'), detail: this.translate.instant('transactionalEmail.list.createdDetail') }));
        } else if (this.state?.message === 'success_updated') {
            setTimeout(() => this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('datastore.successSummary'),
                detail: this.translate.instant('transactionalEmail.list.updatedDetail')
            }));
        }
        this.initExportMenu();
    }

    @ViewChild(Table) table: Table;
    search = '';

    onSearch(value: string) {
        this.search = value;
        this.table ? this.table.reset() : this.loadLazy();
    }

    loadLazy(event: LazyLoadEvent = null) {
        if (event) {
            this.actualEvent = event;
        }
        const start = Math.round(this.actualEvent.first / this.actualEvent.rows);
        const end = this.actualEvent.rows;
        if (this.datastore) {
            if (this.partner) {
                const pageable: Pageable = {
                    page: start,
                    size: end,
                    sort: []
                };
                this.emailService.getAllTransactionalEmailsByPartner(this.datastore.code, this.partner.code, pageable, this.search, 'response').subscribe((res) => {
                    this.totalRecords = +res.headers.get('x-total-count');
                    this.emails = res.body;
                    this.updateExportMenuState();
                });
            }
            else {
                this.emailService.getAllTransactionalEmailsUsingGET1(this.datastore?.code, start, end, [], this.search, 'response').subscribe((res) => {
                    this.totalRecords = +res.headers.get('x-total-count');
                    this.emails = res.body;
                    this.updateExportMenuState();
                });
            }
        }
    }

    remove(email: TransactionalEmailConfDTO) {
        this.confirmationService.confirm({
            message: this.translate.instant('transactionalEmail.list.confirmDeleteMessage', {name: email.name}),
            accept: () => {
                this.emailService.deleteTransactionalEmailUsingDELETE(this.datastore.code, email.id).subscribe(() => {
                    this.loadLazy();
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('datastore.successSummary'),
                        detail: this.translate.instant('transactionalEmail.list.deleteSuccessDetail')
                    });
                }, () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('datastore.errorSummary'),
                        detail: this.translate.instant('landingPage.template.genericErrorDetail')
                    });
                });
            }
        });
    }

    /**
     * Initialise le menu d'export/import
     */
    private initExportMenu() {
        this.exportMenuItems = [
            {
                label: this.translate.instant('subscriptions.importFile'),
                icon: 'fas fa-upload',
                command: () => this.triggerFileUpload()
            },
            {
                label: this.translate.instant('subscriptions.exportSelection'),
                icon: 'fas fa-file-export',
                command: () => this.exportSelectedEmails(),
                disabled: !this.selectedEmails || this.selectedEmails.length === 0
            },
            {
                label: this.translate.instant('subscriptions.exportAll'),
                icon: 'fas fa-download',
                command: () => this.exportAllEmails(),
                disabled: !this.emails || this.emails.length === 0
            }
        ];
    }

    /**
     * Déclenche l'ouverture du sélecteur de fichier
     */
    triggerFileUpload() {
        this.emailFileInput.nativeElement.click();
    }

    /**
     * Gère l'import de fichiers (JSON ou ZIP)
     */
    importFile(event: any) {
        const files: FileList = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        this.uploadingFile = true;
        const file: File = files[0];

        if (file.name.endsWith('.json')) {
            this.handleJsonImport(file);
        } else if (file.name.endsWith('.zip')) {
            this.handleZipImport(file);
        } else {
            this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('datastore.errorSummary'),
                detail: this.translate.instant('subscriptions.unsupportedFormatDetail')
            });
            this.uploadingFile = false;
        }

        // Reset input
        event.target.value = '';
    }

    /**
     * Importe un fichier JSON
     */
    private handleJsonImport(file: File) {
        const reader: FileReader = new FileReader();
        reader.onload = (e: any) => {
            const jsonContent: string = e.target.result;
            this.emailService.importTransactionalEmailJson(this.datastore.code, jsonContent).subscribe(
                (entity: TransactionalEmailConfDTO) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('datastore.successSummary'),
                        detail: this.translate.instant('transactionalEmail.list.importSuccessDetail', {name: entity.name})
                    });
                    this.loadLazy();
                },
                (error) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('datastore.errorSummary'),
                        detail: error.error?.detail || this.translate.instant('subscriptions.importErrorDetail')
                    });
                }
            );
        };
        reader.readAsText(file);
    }

    /**
     * Importe un fichier ZIP
     */
    private handleZipImport(file: File) {
        this.emailService.importTransactionalEmailsZip(this.datastore.code, file).subscribe(
            (entities: TransactionalEmailConfDTO[]) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('transactionalEmail.list.importSuccessMultipleDetail', {count: entities.length})
                });
                this.loadLazy();
            },
            (error) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: error.error?.detail || this.translate.instant('subscriptions.importErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte un seul email en JSON
     */
    exportEmailJson(email: TransactionalEmailConfDTO) {
        this.emailService.exportTransactionalEmailJson(this.datastore.code, email.id).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `transactional-email-${email.code}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('transactionalEmail.list.exportSuccessDetail')
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte plusieurs emails en ZIP
     */
    exportSelectedEmails() {
        if (!this.selectedEmails || this.selectedEmails.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('datastore.warnSummary'),
                detail: this.translate.instant('transactionalEmail.list.noEmailSelectedDetail')
            });
            return;
        }

        const ids = this.selectedEmails.map(c => c.id);
        this.emailService.exportTransactionalEmailsZip(this.datastore.code, ids).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `transactional-emails-export.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('transactionalEmail.list.exportSuccessMultipleDetail', {count: this.selectedEmails.length})
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte tous les emails en ZIP
     */
    exportAllEmails() {
        if (!this.emails || this.emails.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('datastore.warnSummary'),
                detail: this.translate.instant('transactionalEmail.list.noEmailToExportDetail')
            });
            return;
        }

        const ids = this.emails.map(c => c.id);
        this.emailService.exportTransactionalEmailsZip(this.datastore.code, ids).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `transactional-emails-export-all.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('transactionalEmail.list.exportSuccessMultipleDetail', {count: this.emails.length})
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Met à jour l'état des items du menu export
     */
    private updateExportMenuState() {
        if (this.exportMenuItems && this.exportMenuItems.length > 0) {
            // Update "Exporter la sélection" (index 1)
            if (this.exportMenuItems[1]) {
                this.exportMenuItems[1].disabled = !this.selectedEmails || this.selectedEmails.length === 0;
            }
            // Update "Exporter tout" (index 2)
            if (this.exportMenuItems[2]) {
                this.exportMenuItems[2].disabled = !this.emails || this.emails.length === 0;
            }
        }
    }

    onSelectionChange() {
        this.updateExportMenuState();
    }
}
